/**
 * View Store - Manages visualization state and user preferences
 * Handles display mode, grid columns, and visibility toggles
 */

import { create } from 'zustand';

const STORAGE_KEY = 'ai360-view-preferences';

export interface ViewState {
  // Current view mode
  displayMode: 'list' | 'cards' | 'text';
  gridColumns: 1 | 2 | 3;
  
  // Visibility toggles
  showTrustScore: boolean;
  showTags: boolean;
  showActions: boolean;
  compactMode: boolean;
  
  // Actions
  setDisplayMode: (mode: 'list' | 'cards' | 'text') => void;
  setGridColumns: (columns: 1 | 2 | 3) => void;
  toggleTrustScore: () => void;
  toggleTags: () => void;
  toggleActions: () => void;
  toggleCompactMode: () => void;
  resetToDefaults: () => void;
  loadPreferences: () => void;
  initializeFromSettings: (settings: any) => void;
}

// Default state
const defaultState = {
  displayMode: 'cards' as const,
  gridColumns: 2 as const,
  showTrustScore: true,
  showTags: true,
  showActions: true,
  compactMode: false,
};

// Load preferences from localStorage
const loadPreferences = (): Partial<ViewState> => {
  if (typeof localStorage === 'undefined') return {};
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Save preferences to localStorage
const savePreferences = (state: Partial<ViewState>) => {
  if (typeof localStorage === 'undefined') return;
  
  try {
    const toSave = {
      displayMode: state.displayMode,
      gridColumns: state.gridColumns,
      showTrustScore: state.showTrustScore,
      showTags: state.showTags,
      showActions: state.showActions,
      compactMode: state.compactMode,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch {
    // Ignore storage errors
  }
};

export const useViewStore = create<ViewState>((set, get) => {
  // Load initial preferences
  const stored = loadPreferences();
  const initialState = { ...defaultState, ...stored };

  return {
    ...initialState,

    setDisplayMode: (mode) => {
      set({ displayMode: mode });
      savePreferences(get());
    },

    setGridColumns: (columns) => {
      set({ gridColumns: columns });
      savePreferences(get());
    },

    toggleTrustScore: () => {
      set((state) => ({ showTrustScore: !state.showTrustScore }));
      savePreferences(get());
    },

    toggleTags: () => {
      set((state) => ({ showTags: !state.showTags }));
      savePreferences(get());
    },

    toggleActions: () => {
      set((state) => ({ showActions: !state.showActions }));
      savePreferences(get());
    },

    toggleCompactMode: () => {
      set((state) => ({ compactMode: !state.compactMode }));
      savePreferences(get());
    },

    resetToDefaults: () => {
      set(defaultState);
      savePreferences(defaultState);
    },

    loadPreferences: () => {
      const stored = loadPreferences();
      set({ ...defaultState, ...stored });
    },

    initializeFromSettings: (settings) => {
      if (!settings) return;
      
      // Map WordPress settings to viewStore state
      const newState: Partial<ViewState> = {};
      
      // Display mode from results.displayMode or default_view
      if (settings.results?.displayMode) {
        newState.displayMode = settings.results.displayMode;
      } else if (settings.default_view) {
        newState.displayMode = settings.default_view;
      }
      
      // Grid columns from results.gridColumns or default_columns with validation
      if (settings.results?.gridColumns) {
        const cols = settings.results.gridColumns;
        if (cols === 1 || cols === 2 || cols === 3) {
          newState.gridColumns = cols;
        }
      } else if (settings.default_columns) {
        const cols = parseInt(settings.default_columns, 10);
        if (cols === 1 || cols === 2 || cols === 3) {
          newState.gridColumns = cols;
        }
      }
      
      // Show TrustScore from results.showTrustScore or showTrustScore
      if (settings.results?.showTrustScore !== undefined) {
        newState.showTrustScore = settings.results.showTrustScore;
      } else if (settings.showTrustScore !== undefined) {
        newState.showTrustScore = settings.showTrustScore;
      }
      
      // Show tags from results.showTags
      if (settings.results?.showTags !== undefined) {
        newState.showTags = settings.results.showTags;
      }
      
      // Compact mode - no direct setting, keep default
      
      // Apply settings, but allow localStorage to override if rememberUserPreferences is enabled
      const shouldRememberPreferences = settings.userControls?.rememberUserPreferences !== false;
      
      if (shouldRememberPreferences) {
        const stored = loadPreferences();
        set({ ...defaultState, ...newState, ...stored });
      } else {
        set({ ...defaultState, ...newState });
      }
    },
  };
});
